import { useMemo, useState } from 'react'
import {
  Mail,
  Send,
  User,
  MessageSquare,
  Tag,
  CheckCircle,
  AlertCircle,
  Facebook,
  Twitter,
  Instagram,
  Youtube,
  Globe,
  Clock3,
  ShieldCheck,
  Loader2,
  ExternalLink
} from 'lucide-react'
import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { db } from '../firebase/config'

const MAX_MESSAGE_LENGTH = 1200

const subjects = [
  { value: '', label: 'Sélectionnez un sujet' },
  { value: 'general', label: '💬 Question générale' },
  { value: 'technique', label: '🔧 Problème technique' },
  { value: 'suggestion', label: '💡 Suggestion' },
  { value: 'dedicace', label: '🎵 Demande de dédicace' },
  { value: 'partenariat', label: '🤝 Partenariat' },
  { value: 'autre', label: '📋 Autre' }
]

const socialLinks = [
  {
    name: 'Facebook',
    icon: <Facebook size={22} />,
    url: 'https://www.facebook.com/JHallyday',
    color: '#1877f2'
  },
  {
    name: 'Twitter / X',
    icon: <Twitter size={22} />,
    url: 'https://twitter.com/100johnny3',
    color: '#1da1f2'
  },
  {
    name: 'Instagram',
    icon: <Instagram size={22} />,
    url: 'https://www.instagram.com/sjhjohnny/',
    color: '#e4405f'
  },
  {
    name: 'YouTube',
    icon: <Youtube size={22} />,
    url: 'https://www.youtube.com/@johnnysjh',
    color: '#ff0000'
  },
  {
    name: 'Site officiel',
    icon: <Globe size={22} />,
    url: 'https://100pour100johnny.com',
    color: '#00d9ff'
  }
]

const initialForm = {
  name: '',
  email: '',
  subject: '',
  message: '',
  website: ''
}

function ContactPage() {
  const [formData, setFormData] = useState(initialForm)
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState('')

  const selectedSubject = useMemo(
    () => subjects.find(item => item.value === formData.subject)?.label || '',
    [formData.subject]
  )

  const handleChange = e => {
    const { name, value } = e.target

    if (name === 'message' && value.length > MAX_MESSAGE_LENGTH) return

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (submitStatus) {
      setSubmitStatus(null)
      setErrorMessage('')
    }
  }

  const resetFeedback = () => {
    window.setTimeout(() => {
      setSubmitStatus(null)
      setErrorMessage('')
    }, 7000)
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (formData.website.trim()) return

    const name = formData.name.trim()
    const email = formData.email.trim().toLowerCase()
    const message = formData.message.trim()

    if (!name || !email || !formData.subject || !message) {
      setSubmitStatus('error')
      setErrorMessage('Merci de remplir tous les champs obligatoires.')
      resetFeedback()
      return
    }

    if (name.length < 2) {
      setSubmitStatus('error')
      setErrorMessage('Veuillez indiquer un nom valide.')
      resetFeedback()
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(email)) {
      setSubmitStatus('error')
      setErrorMessage('Veuillez entrer une adresse email valide.')
      resetFeedback()
      return
    }

    if (message.length < 5) {
      setSubmitStatus('error')
      setErrorMessage('Votre message est trop court.')
      resetFeedback()
      return
    }

    setSubmitting(true)
    setSubmitStatus(null)
    setErrorMessage('')

    try {
      await addDoc(collection(db, '100johnny', 'main', 'messages_contact'), {
        name,
        email,
        subject: formData.subject,
        subjectLabel: selectedSubject,
        message,
        status: 'new',
        source: 'web-contact',
        createdAt: serverTimestamp(),
        userAgent: navigator.userAgent,
        language: navigator.language || 'fr-FR'
      })

      setFormData(initialForm)
      setSubmitStatus('success')
      resetFeedback()
    } catch (error) {
      console.error('Erreur Firestore formulaire de contact :', error)
      setSubmitStatus('error')
      setErrorMessage(
        'Impossible d’enregistrer votre message. Vous pouvez utiliser directement notre adresse email ci-dessous.'
      )
      resetFeedback()
    } finally {
      setSubmitting(false)
    }
  }

  const openEmailFallback = () => {
    const subject = encodeURIComponent(
      formData.subject
        ? `${selectedSubject.replace(/^[^ ]+ /, '')} — Contact radio`
        : 'Contact — Radio 100% Johnny'
    )

    const body = encodeURIComponent(
      `Bonjour,\n\nNom : ${formData.name.trim()}\nEmail : ${formData.email.trim()}\n\n${formData.message.trim()}\n\nEnvoyé depuis Radio 100% Johnny.`
    )

    window.location.href =
      `mailto:contact@100pour100johnny.com?subject=${subject}&body=${body}`
  }

  return (
    <div className="page active">
      <div
        className="container contact-page-container"
        style={{
          width: '100%',
          maxWidth: 980,
          margin: '0 auto',
          paddingBottom: 40,
          boxSizing: 'border-box'
        }}
      >
        {/* HEADER */}
        <div className="page-header">
          <div className="page-badge">💬 CONTACT</div>
          <h1 className="page-title">Contactez-nous</h1>
          <p className="page-subtitle">
            Une question, une suggestion ou un problème avec la radio ?
          </p>
        </div>

        {/* INFOS */}
        <div
          className="glass-card"
          style={{
            marginBottom: 22,
            padding: 'clamp(16px, 3vw, 24px)',
            border: '1px solid rgba(0,217,255,.16)',
            boxSizing: 'border-box',
            width: '100%'
          }}
        >
          <div className="contact-info-grid">
            <InfoItem
              icon={<Mail size={21} color="#00d9ff" />}
              title="Une réponse humaine"
              text="Écrivez-nous directement."
            />
            <InfoItem
              icon={<Clock3 size={21} color="#ffd700" />}
              title="Réponse sous 48 h"
              text="Selon le sujet et les horaires."
            />
            <InfoItem
              icon={<ShieldCheck size={21} color="#00d9ff" />}
              title="Données protégées"
              text="Utilisées uniquement pour répondre."
            />
          </div>
        </div>

        {/* IMPORTANT :
            Téléphone = 1 colonne.
            Tablette = 2 colonnes.
            Le CSS est défini plus bas avec une vraie media query.
        */}
        <div className="contact-main-layout">
          {/* FORMULAIRE */}
          <section
            className="glass-card contact-form-card"
            style={{
              padding: 'clamp(18px, 3vw, 30px)',
              boxSizing: 'border-box',
              width: '100%',
              minWidth: 0
            }}
          >
            <div style={{ marginBottom: 22 }}>
              <div className="contact-kicker">NOUS ÉCRIRE</div>
              <h2 className="contact-section-title">Votre message</h2>
              <p className="contact-section-description">
                Remplissez le formulaire et envoyez-nous votre demande.
              </p>
            </div>

            {submitStatus === 'success' && (
              <div className="contact-success" role="status">
                <CheckCircle size={25} color="#00ff9d" />
                <div>
                  <strong>Message envoyé avec succès !</strong>
                  <p>
                    Votre message a bien été enregistré. Nous vous répondrons
                    dans les meilleurs délais.
                  </p>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="contact-error" role="alert">
                <AlertCircle size={25} color="#ff6666" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong>Envoi impossible</strong>
                  <p>{errorMessage || 'Vérifiez les champs du formulaire.'}</p>
                  <button
                    type="button"
                    onClick={openEmailFallback}
                    className="contact-email-fallback"
                  >
                    <Mail size={14} />
                    Envoyer par email
                  </button>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              {/* Anti-bot */}
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: '-10000px',
                  width: 1,
                  height: 1,
                  overflow: 'hidden'
                }}
              >
                <label htmlFor="website">Ne pas remplir</label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  tabIndex="-1"
                  autoComplete="off"
                  value={formData.website}
                  onChange={handleChange}
                />
              </div>

              {/* TOUJOURS VERTICAL : NOM PUIS EMAIL */}
              <div className="contact-form-fields">
                <Field label="Nom complet" icon={<User size={17} />} required>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Votre nom et prénom"
                    autoComplete="name"
                    maxLength={80}
                    required
                  />
                </Field>

                <Field label="Adresse email" icon={<Mail size={17} />} required>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="vous@exemple.com"
                    autoComplete="email"
                    maxLength={120}
                    required
                  />
                </Field>

                <Field label="Sujet" icon={<Tag size={17} />} required>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                  >
                    {subjects.map(subject => (
                      <option
                        key={subject.value}
                        value={subject.value}
                        style={{
                          background: '#111820',
                          color: '#fff'
                        }}
                      >
                        {subject.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  label="Votre message"
                  icon={<MessageSquare size={17} />}
                  required
                >
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Écrivez votre message ici..."
                    rows={7}
                    maxLength={MAX_MESSAGE_LENGTH}
                    required
                  />

                  <div className="contact-counter">
                    <span>Minimum 5 caractères</span>
                    <span
                      className={
                        formData.message.length >
                        MAX_MESSAGE_LENGTH * 0.9
                          ? 'warning'
                          : ''
                      }
                    >
                      {formData.message.length}/{MAX_MESSAGE_LENGTH}
                    </span>
                  </div>
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="btn btn-primary contact-submit"
              >
                {submitting ? (
                  <>
                    <Loader2 className="contact-spinner" size={19} />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send size={19} />
                    Envoyer le message
                  </>
                )}
              </button>

              <p className="contact-legal">
                En envoyant ce formulaire, vous acceptez que les informations
                soient utilisées pour traiter votre demande.
              </p>
            </form>
          </section>

          {/* COLONNE DROITE SUR TABLETTE / SOUS LE FORMULAIRE SUR TELEPHONE */}
          <aside className="contact-sidebar">
            {/* CONTACT DIRECT */}
            <div className="glass-card contact-side-card">
              <div className="contact-kicker">CONTACT DIRECT</div>
              <h2 className="contact-section-title">Nos coordonnées</h2>

              <a
                href="mailto:contact@100pour100johnny.com"
                className="contact-email-card"
              >
                <Mail size={28} />
                <span>contact@100pour100johnny.com</span>
              </a>

              <button
                type="button"
                onClick={() => {
                  window.location.href =
                    'mailto:contact@100pour100johnny.com'
                }}
                className="contact-open-email"
              >
                <Mail size={19} />
                Ouvrir ma messagerie
              </button>
            </div>

            {/* COMMUNAUTÉ */}
            <div className="glass-card contact-side-card">
              <div className="contact-kicker">COMMUNAUTÉ</div>
              <h2 className="contact-section-title">Suivez-nous</h2>

              <div className="contact-social-grid">
                {socialLinks.map(social => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Ouvrir ${social.name}`}
                    className="content-card contact-social-link"
                  >
                    <span
                      style={{
                        color: social.color,
                        display: 'flex',
                        flexShrink: 0
                      }}
                    >
                      {social.icon}
                    </span>

                    <span className="contact-social-name">
                      {social.name}
                    </span>

                    <ExternalLink
                      size={15}
                      className="contact-social-external"
                    />
                  </a>
                ))}
              </div>
            </div>

            {/* CONFIDENTIALITÉ */}
            <div className="contact-privacy">
              <ShieldCheck size={20} color="#00d9ff" />
              <p>
                Vos informations servent uniquement au traitement de votre
                demande et ne sont pas affichées publiquement.
              </p>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        /*
         * ==========================================================
         * CONTACT PAGE
         * Téléphone : 1 colonne
         * Tablette : 2 colonnes
         * ==========================================================
         */

        .contact-page-container {
          overflow: visible !important;
        }

        .contact-main-layout {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 22px !important;
          width: 100% !important;
          max-width: 100% !important;
          align-items: start !important;
        }

        .contact-form-card,
        .contact-sidebar {
          width: 100% !important;
          min-width: 0 !important;
          max-width: 100% !important;
          box-sizing: border-box !important;
        }

        /*
         * Champs TOUJOURS l'un sous l'autre.
         * Même sur tablette.
         */
        .contact-form-fields {
          display: flex !important;
          flex-direction: column !important;
          width: 100% !important;
          min-width: 0 !important;
          gap: 0 !important;
        }

        .contact-form-fields .form-group {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          float: none !important;
          clear: both !important;
        }

        .contact-form-fields input,
        .contact-form-fields select,
        .contact-form-fields textarea {
          display: block !important;
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
        }

        .contact-sidebar {
          display: flex !important;
          flex-direction: column !important;
          gap: 16px !important;
        }

        .contact-side-card {
          width: 100% !important;
          box-sizing: border-box !important;
        }

        .contact-info-grid {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 14px !important;
        }

        .contact-social-grid {
          display: grid !important;
          grid-template-columns: 1fr !important;
          gap: 9px !important;
        }

        .contact-social-link {
          width: 100% !important;
          min-width: 0 !important;
          box-sizing: border-box !important;
          display: flex !important;
          align-items: center !important;
          gap: 10px !important;
          padding: 13px !important;
          text-decoration: none !important;
        }

        .contact-social-name {
          color: #fff !important;
          font-size: .82rem !important;
          font-weight: 700 !important;
          overflow: hidden !important;
          text-overflow: ellipsis !important;
          white-space: nowrap !important;
          min-width: 0 !important;
        }

        .contact-social-external {
          margin-left: auto !important;
          color: rgba(255,255,255,.35) !important;
          flex-shrink: 0 !important;
        }

        .contact-kicker {
          color: #00d9ff;
          font-size: .68rem;
          font-weight: 900;
          letter-spacing: .12em;
          margin-bottom: 5px;
        }

        .contact-section-title {
          margin: 0;
          font-size: 1.35rem;
          font-weight: 850;
          background: linear-gradient(135deg,#00d9ff,#ffd700);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .contact-section-description {
          margin: 7px 0 0;
          color: rgba(255,255,255,.48);
          font-size: .8rem;
          line-height: 1.5;
        }

        .contact-form-fields input,
        .contact-form-fields select,
        .contact-form-fields textarea {
          background: rgba(255,255,255,.045) !important;
          border: 1px solid rgba(255,255,255,.12) !important;
          border-radius: 12px !important;
          color: #fff !important;
          padding: 12px 13px !important;
          outline: none !important;
          font-size: .9rem !important;
          font-family: inherit !important;
        }

        .contact-form-fields input:focus,
        .contact-form-fields select:focus,
        .contact-form-fields textarea:focus {
          border-color: rgba(0,217,255,.65) !important;
          box-shadow: 0 0 0 3px rgba(0,217,255,.08) !important;
        }

        .contact-form-fields textarea {
          resize: vertical !important;
          min-height: 150px !important;
          line-height: 1.55 !important;
        }

        .contact-counter {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin-top: 7px;
          color: rgba(255,255,255,.42);
          font-size: .75rem;
        }

        .contact-counter .warning {
          color: #ffd700;
        }

        .contact-submit {
          width: 100% !important;
          min-height: 54px !important;
          margin-top: 5px !important;
          border-radius: 14px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 8px !important;
        }

        .contact-spinner {
          animation: contactSpin 1s linear infinite;
        }

        .contact-legal {
          margin: 12px 0 0;
          text-align: center;
          color: rgba(255,255,255,.38);
          font-size: .73rem;
          line-height: 1.5;
        }

        .contact-email-card {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 15px;
          margin-top: 17px;
          border-radius: 13px;
          background: rgba(0,217,255,.06);
          border: 1px solid rgba(0,217,255,.14);
          color: #00d9ff;
          text-decoration: none;
          font-weight: 700;
          line-height: 1.35;
          word-break: break-word;
        }

        .contact-open-email {
          width: 100%;
          margin-top: 10px;
          min-height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 7px;
          border-radius: 11px;
          border: 1px solid rgba(0,217,255,.2);
          background: rgba(0,217,255,.07);
          color: #00d9ff;
          font-size: .8rem;
          font-weight: 800;
          cursor: pointer;
        }

        .contact-success,
        .contact-error {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 15px;
          margin-bottom: 18px;
          border-radius: 13px;
        }

        .contact-success {
          background: rgba(0,255,157,.07);
          border: 1px solid rgba(0,255,157,.25);
        }

        .contact-success strong {
          color: #00ff9d;
        }

        .contact-error {
          background: rgba(255,68,68,.07);
          border: 1px solid rgba(255,68,68,.25);
        }

        .contact-error strong {
          color: #ff7777;
        }

        .contact-success p,
        .contact-error p {
          margin: 5px 0 0;
          color: rgba(255,255,255,.58);
          font-size: .78rem;
          line-height: 1.5;
        }

        .contact-email-fallback {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-top: 9px;
          padding: 8px 11px;
          border-radius: 9px;
          border: 1px solid rgba(0,217,255,.2);
          background: rgba(0,217,255,.07);
          color: #00d9ff;
          font-size: .74rem;
          font-weight: 800;
          cursor: pointer;
        }

        .contact-privacy {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 16px;
          border-radius: 14px;
          background: linear-gradient(
            135deg,
            rgba(0,217,255,.06),
            rgba(255,215,0,.04)
          );
          border: 1px solid rgba(255,255,255,.08);
          box-sizing: border-box;
        }

        .contact-privacy p {
          margin: 0;
          color: rgba(255,255,255,.55);
          font-size: .78rem;
          line-height: 1.55;
        }

        .contact-info-grid > * {
          min-width: 0;
        }

        /*
         * ==========================================================
         * TELEPHONE : UNE SEULE COLONNE
         * Jusqu'à 819px inclus.
         * ==========================================================
         */
        @media (max-width: 819px) {
          .contact-main-layout {
            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .contact-form-card,
          .contact-sidebar {
            grid-column: 1 !important;
            width: 100% !important;
          }

          .contact-info-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .contact-social-grid {
            grid-template-columns: minmax(0, 1fr) !important;
          }

          .contact-page-container {
            padding-left: 12px !important;
            padding-right: 12px !important;
          }
        }

        /*
         * ==========================================================
         * TABLETTE : DEUX COLONNES
         * À partir de 820px.
         * ==========================================================
         */
        @media (min-width: 820px) {
          .contact-main-layout {
            display: grid !important;
            grid-template-columns:
              minmax(0, 1.25fr)
              minmax(280px, .75fr) !important;
            gap: 22px !important;
          }

          .contact-form-card {
            grid-column: 1 !important;
          }

          .contact-sidebar {
            grid-column: 2 !important;
          }

          .contact-info-grid {
            grid-template-columns:
              repeat(3, minmax(0, 1fr)) !important;
          }

          .contact-social-grid {
            grid-template-columns:
              repeat(2, minmax(0, 1fr)) !important;
          }
        }

        /*
         * Grand écran : on garde deux colonnes mais on agrandit
         * légèrement l'espace disponible.
         */
        @media (min-width: 1100px) {
          .contact-main-layout {
            grid-template-columns:
              minmax(0, 1.35fr)
              minmax(320px, .65fr) !important;
          }
        }

        @keyframes contactSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}

function InfoItem({ icon, title, text }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        minWidth: 0
      }}
    >
      <div
        style={{
          width: 43,
          height: 43,
          flexShrink: 0,
          borderRadius: 12,
          background: 'rgba(0,217,255,.08)',
          border: '1px solid rgba(0,217,255,.12)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {icon}
      </div>

      <div style={{ minWidth: 0 }}>
        <strong
          style={{
            display: 'block',
            color: '#fff',
            fontSize: '.82rem',
            marginBottom: 3
          }}
        >
          {title}
        </strong>

        <span
          style={{
            color: 'rgba(255,255,255,.42)',
            fontSize: '.72rem'
          }}
        >
          {text}
        </span>
      </div>
    </div>
  )
}

function Field({ label, icon, required, children }) {
  return (
    <div
      className="form-group"
      style={{
        marginBottom: 16,
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box'
      }}
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          marginBottom: 8,
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <span
          style={{
            color: '#00d9ff',
            display: 'flex',
            flexShrink: 0
          }}
        >
          {icon}
        </span>

        <span>{label}</span>

        {required && (
          <span
            style={{
              color: '#ffd700',
              marginLeft: 1
            }}
          >
            *
          </span>
        )}
      </label>

      {children}
    </div>
  )
}

export default ContactPage